import { uuidv7 } from 'uuidv7';
import { client } from '@dealstack/shared/lib/backendClient/client.gen';
import { getGlobalConfig } from '@dealstack/frontend/shared/globalConfig';
import { withHttpStatus } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { getProjectIdsFromUrl } from '@dealstack/frontend/shared/dataExchange/ApiLayer/getProjectIdsFromUrl';

/**
 * Configures the Hey API generated backend client (`@hey-api/client-fetch`).
 * Call once during application initialization, before any generated SDK
 * function runs.
 *
 * Responsibilities (mirrors the legacy `apiClient` fetch wrapper):
 *   1. Base URL from the runtime global config (`window.dsConfig.apiUrl`).
 *   2. `credentials: 'include'` so the auth cookie is sent (this backend uses
 *      cookie auth — there is no bearer token).
 *   3. An `X-Request-Id` header on every request.
 *   4. `deal-id` / `mip-id` headers derived from the current URL, so deal- and
 *      mip-scoped endpoints (whose guards read those headers) work without the
 *      caller passing them explicitly — mirrors the legacy `createHeaders`.
 *   5. Unwraps the backend's global `{ payload: T }` response envelope so the
 *      data returned by SDK functions matches the generated response types.
 *
 * (4) exists because the backend's `ResponseInterceptor` wraps every response
 * in `{ payload }` at runtime, but the OpenAPI annotations declare the
 * unwrapped type. Unwrapping here realigns runtime with the generated types.
 * The ~5 `@RawPayload()` endpoints have no `payload` key and pass through
 * untouched.
 */
let configured = false;

export function configureBackendClient(): void {
	// Guard against duplicate interceptor registration if called more than once
	// (e.g. HMR re-evaluating the entry module, or tests). setConfig is
	// idempotent; interceptors.use is not.
	if (configured) {
		return;
	}
	configured = true;

	client.setConfig({
		baseUrl: getGlobalConfig().apiUrl,
		credentials: 'include',
	});

	client.interceptors.request.use((request) => {
		if (!request.headers.has('X-Request-Id')) {
			request.headers.set('X-Request-Id', uuidv7());
		}

		// Inject deal-id / mip-id from the URL (the legacy layer does this via
		// createHeaders). Don't overwrite a header the caller set explicitly.
		const { dealId, mipId } = getProjectIdsFromUrl();
		if (dealId && !request.headers.has('deal-id')) {
			request.headers.set('deal-id', dealId);
		}
		if (mipId && !request.headers.has('mip-id')) {
			request.headers.set('mip-id', mipId);
		}

		return request;
	});

	client.interceptors.response.use(async (response) => {
		const contentType = response.headers.get('Content-Type') ?? '';
		if (!contentType.includes('application/json')) {
			return response;
		}

		const body: unknown = await response
			.clone()
			.json()
			.catch(() => undefined);

		if (body && typeof body === 'object' && 'payload' in body) {
			return new Response(JSON.stringify((body as { payload: unknown }).payload), {
				status: response.status,
				statusText: response.statusText,
				headers: response.headers,
			});
		}

		return response;
	});

	// The error body names no status; put the response's beside it, as `statusCode`, for the callers that branch on it.
	client.interceptors.error.use((error, response) => withHttpStatus(error, response?.status));
}
